/**
 * RecipeGraph — what a bootstrap card shows when it has no live UI sample.
 *
 * Two kinds of card land here: `render:"spec"` entries (the host-owned capture button and
 * the L4 Patterns) and any component whose preview sample is missing. Both used
 * to render an empty dashed box reading "no distinct native impl yet", which
 * teaches nothing about the thing the card documents.
 *
 * Everything below is derived from the REGISTRY ENTRY alone — no per-component
 * table to keep in sync:
 *   ports   a component with typed ports → its slots ◂ in above, the component,
 *           its signals ▸ out below, each labelled with the registry payload;
 *   recipe  a pattern → the recipe its own description names, parsed as stages
 *           (`→`, or `;`) of members composed with `+`.
 *
 * Same visual language as the detail view's SignalSlotGraph: SOLID = sync (a
 * slot call, a member composed into the same frame), DASHED = async (a signal,
 * a step that only lands after a host round-trip), and a host node is dashed
 * because it has no business UI. Deliberately schematic — it has to read at
 * ~320px card width, so long step text wraps to two lines and then truncates
 * (the full text stays in the node's tooltip).
 */
import { KIT } from '../viewir/registry';

const C = { ui: '#5856d6', host: '#6e6e73', step: '#B45309', self: '#6E59F2', ink: '#1d1d1f', dim: '#8e8e93' };

const W = 280;
const PAD = 10;
const NODE_W = W - PAD * 2;
const GAP_JOIN = 15; // between members of ONE band (the `+` spine)
const GAP_BAND = 24; // between bands (room for the flow arrow)

const KIT_IDS = Object.keys(KIT).filter((id) => id !== 'host');

/** A step that names a kit component is a UI node; one that names the host is a host node. */
function kindOf(label) {
  if (/\bhost\b/i.test(label)) return 'host';
  return KIT_IDS.some((id) => new RegExp(`\\b${id}\\b`).test(label)) ? 'ui' : 'step';
}

/** Greedy wrap to at most `maxLines`; the overflow becomes an ellipsis. */
function wrap(text, maxChars = 44, maxLines = 2) {
  const lines = [''];
  for (const word of String(text).split(/\s+/).filter(Boolean)) {
    const i = lines.length - 1;
    const merged = lines[i] ? `${lines[i]} ${word}` : word;
    if (merged.length <= maxChars) { lines[i] = merged; continue; }
    if (lines.length < maxLines) { lines.push(word); continue; }
    lines[i] = `${lines[i].slice(0, maxChars - 1)}…`;
    break;
  }
  return lines;
}

/**
 * The recipe sentence out of a pattern's description: everything after
 * "Recipe:" up to the first full stop or em-dash aside (the rest of the
 * description is prose about the recipe, not part of it).
 */
function recipeText(description = '') {
  const m = /Recipe:\s*([\s\S]*)/i.exec(description);
  return (m ? m[1] : description).split(/\.\s|\s—\s/)[0].replace(/\.$/, '').trim();
}

/** Stages of composed members: `→` (or `;`) separates stages, `+` composes. */
function recipeBands(comp) {
  const text = recipeText(comp.description);
  const flow = text.includes('→') ? text.split('→') : text.split(';');
  const stages = flow.map((s) => s.trim()).filter(Boolean);
  return stages.map((stage, i) => ({
    join: '+',
    next: i < stages.length - 1 ? 'dashed' : null,
    nodes: stage.split('+').map((m) => m.trim()).filter(Boolean).map((label) => ({ label, kind: kindOf(label) })),
  }));
}

/** Slots ◂ in → the component → signals ▸ out. */
function portBands(comp) {
  const slots = comp.slots || [];
  const signals = comp.signals || [];
  const bands = [];
  if (slots.length) {
    bands.push({ join: null, next: 'solid', nodes: slots.map((s) => ({ label: `${s.name} ◂ in`, kind: 'port', mono: true })) });
  }
  bands.push({ join: null, next: signals.length ? 'dashed' : null, nodes: [{ label: comp.id, kind: 'self' }] });
  if (signals.length) {
    bands.push({
      join: null,
      next: null,
      nodes: signals.map((s) => ({ label: `${s.name} ▸ out ${s.payload || ''}`.trim(), kind: 'port', mono: true })),
    });
  }
  return bands;
}

const STROKE = { ui: C.ui, host: C.host, step: C.step, port: C.ui, self: C.self };

export default function RecipeGraph({ comp }) {
  const ports = Boolean(comp.signals?.length || comp.slots?.length);
  const bands = ports ? portBands(comp) : recipeBands(comp);

  // Vertical layout: one pass, top to bottom.
  const rows = [];
  const links = [];
  let y = 4;
  bands.forEach((band, bi) => {
    band.nodes.forEach((node, ni) => {
      const lines = wrap(node.label);
      const h = lines.length > 1 ? 33 : 22;
      const prev = rows[rows.length - 1];
      // inside a band: a solid spine, carrying `+` when the members COMPOSE.
      if (prev && ni > 0) links.push({ from: prev.y + prev.h, to: y, mode: 'solid', glyph: band.join });
      // between bands: the flow edge, sync or async per the band it leaves.
      if (prev && ni === 0 && bi > 0) links.push({ from: prev.y + prev.h, to: y, mode: bands[bi - 1].next || 'dashed', arrow: true });
      rows.push({ ...node, lines, y, h });
      y += h + (ni < band.nodes.length - 1 ? GAP_JOIN : 0);
    });
    if (bi < bands.length - 1) y += GAP_BAND;
  });
  const H = y + 4;
  const mid = W / 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', lineHeight: 1.4 }}>
        {ports ? 'No live render — the ports it exposes:' : 'No live render — the recipe it seals:'}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img">
        <defs>
          <marker id="viRecipeArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={C.dim} />
          </marker>
        </defs>

        {links.map((l, i) => (
          <g key={`l-${i}`}>
            <line
              x1={mid} y1={l.from + 2} x2={mid} y2={l.to - 2}
              stroke={C.dim} strokeWidth="1.2"
              strokeDasharray={l.mode === 'solid' ? undefined : '4 3'}
              markerEnd={l.arrow ? 'url(#viRecipeArrow)' : undefined}
            />
            {l.glyph ? (
              <text x={mid + 6} y={(l.from + l.to) / 2 + 3.5} fontSize="10" fontWeight="700" fill={C.dim}>{l.glyph}</text>
            ) : null}
          </g>
        ))}

        {rows.map((r, i) => (
          <g key={`n-${i}`}>
            <rect
              x={PAD} y={r.y} width={NODE_W} height={r.h} rx="7"
              fill="#fff" stroke={STROKE[r.kind] || C.dim}
              strokeWidth={r.kind === 'self' ? 1.7 : 1}
              strokeDasharray={r.kind === 'host' ? '5 4' : undefined}
            />
            <title>{r.label}</title>
            {r.lines.map((line, li) => (
              <text
                key={li}
                x={PAD + 9}
                y={r.y + (r.lines.length > 1 ? 14 + li * 12 : 14.5)}
                fontSize="9.5"
                fontWeight={r.kind === 'self' ? 700 : 400}
                fill={r.kind === 'step' ? C.ink : (STROKE[r.kind] || C.ink)}
                fontFamily={r.mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit'}
              >{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 9.5, color: 'var(--text-3)' }}>
        <span>
          <svg width="18" height="7" style={{ verticalAlign: 'middle' }}><line x1="0" y1="3.5" x2="18" y2="3.5" stroke="currentColor" strokeWidth="1.2" /></svg>{' '}
          {ports ? 'sync · slot call' : 'sync · composed'}
        </span>
        <span>
          <svg width="18" height="7" style={{ verticalAlign: 'middle' }}><line x1="0" y1="3.5" x2="18" y2="3.5" stroke="currentColor" strokeWidth="1.2" strokeDasharray="4 3" /></svg>{' '}
          {ports ? 'async · signal out' : 'async · next step'}
        </span>
        <span>dashed box = host</span>
      </div>
    </div>
  );
}
